<template>
  <div class="dashboard container">
    <v-breadcrumbs>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'inventoryReservation', params: {} }"
      >
        Inventory Reservations
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item active> Dashboard </v-breadcrumbs-item>
    </v-breadcrumbs>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-if="!loading">
      <!-- Summary Cards -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" outlined>
            <v-card-title class="pb-1">
              <v-icon color="blue" class="mr-2">mdi-bookmark-multiple</v-icon>
              <span class="text-subtitle-1">Active Reservations</span>
            </v-card-title>
            <v-card-text>
              <span class="text-h4 font-weight-bold">
                {{ formatNumber(activeReservationCount) }}
              </span>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" outlined>
            <v-card-title class="pb-1">
              <v-icon color="orange" class="mr-2">mdi-weight</v-icon>
              <span class="text-subtitle-1">Total Reserved Weight</span>
            </v-card-title>
            <v-card-text>
              <span class="text-h4 font-weight-bold">
                {{ formatNumber(totalReservedWeight) }}
              </span>
              <span class="text-caption ml-1">kg</span>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" outlined>
            <v-card-title class="pb-1">
              <v-icon color="red" class="mr-2">mdi-alert-circle</v-icon>
              <span class="text-subtitle-1">Open Issues</span>
            </v-card-title>
            <v-card-text>
              <span class="text-h4 font-weight-bold">
                {{ formatNumber(openIssueCount) }}
              </span>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" outlined>
            <v-card-title class="pb-1">
              <v-icon color="amber" class="mr-2">mdi-clock-alert</v-icon>
              <span class="text-subtitle-1">Stale Reservations</span>
            </v-card-title>
            <v-card-text>
              <span class="text-h4 font-weight-bold">
                {{ formatNumber(staleReservationCount) }}
              </span>
              <span class="text-caption ml-1">> 7 days pending</span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Issues Alert -->
      <v-alert
        v-if="openIssueCount > 0"
        type="warning"
        prominent
        outlined
        class="mb-4"
      >
        <v-row align="center">
          <v-col class="grow">
            There {{ openIssueCount === 1 ? "is" : "are" }}
            <strong>{{ openIssueCount }}</strong>
            open inventory {{ openIssueCount === 1 ? "issue" : "issues" }}
            requiring attention (NEEDS_REVIEW or BACKORDER).
          </v-col>
          <v-col class="shrink">
            <v-btn
              color="warning"
              outlined
              @click="navigateToIssues"
            >
              View Issues
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Product Reservation Table -->
      <v-card outlined>
        <v-card-title> Product Reservation Overview </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="productReservationRows"
            :items-per-page="10"
            item-key="productId"
            class="elevation-0"
            :hide-default-header="
              !productReservationRows || productReservationRows.length < 1
            "
            :hide-default-footer="
              !productReservationRows || productReservationRows.length < 1
            "
          >
            <template v-slot:item.totalAvailable="{ item }">
              {{ formatNumber(item.totalAvailable) }}
            </template>

            <template v-slot:item.totalReserved="{ item }">
              {{ formatNumber(item.totalReserved) }}
            </template>

            <template v-slot:item.freeAvailable="{ item }">
              {{ formatNumber(item.freeAvailable) }}
            </template>

            <template v-slot:item.reservationPercent="{ item }">
              <v-chip
                :color="getReservationColor(item.reservationPercent)"
                dark
                small
              >
                {{ item.reservationPercent.toFixed(1) }}%
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "InventoryReservationDashboard",

  data() {
    return {
      loading: true,
      headers: [
        {
          text: "Product Name",
          value: "productName",
          align: "start",
          sortable: true,
        },
        {
          text: "Total Available (kg)",
          value: "totalAvailable",
          align: "end",
          sortable: true,
        },
        {
          text: "Total Reserved (kg)",
          value: "totalReserved",
          align: "end",
          sortable: true,
        },
        {
          text: "Free Available (kg)",
          value: "freeAvailable",
          align: "end",
          sortable: true,
        },
        {
          text: "Reservation %",
          value: "reservationPercent",
          align: "center",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    dashboardSummary() {
      return this.$store.state.inventoryReservation.dashboardSummary || [];
    },

    staleReservations() {
      return this.$store.state.inventoryReservation.staleReservations || [];
    },

    issues() {
      return this.$store.state.inventoryReservation.issues || [];
    },

    activeReservationCount() {
      return this.dashboardSummary.reduce(
        (sum, item) => sum + (item.reservationCount || 0),
        0
      );
    },

    totalReservedWeight() {
      return this.dashboardSummary.reduce(
        (sum, item) => sum + (item.totalReserved || 0),
        0
      );
    },

    openIssueCount() {
      return this.issues.filter(
        (issue) =>
          issue.status === "NEEDS_REVIEW" || issue.status === "BACKORDER"
      ).length;
    },

    staleReservationCount() {
      return this.staleReservations.length;
    },

    productReservationRows() {
      return this.dashboardSummary.map((item) => {
        const totalAvailable = item.totalAvailable || 0;
        const totalReserved = item.totalReserved || 0;
        const freeAvailable = totalAvailable - totalReserved;
        const reservationPercent =
          totalAvailable > 0 ? (totalReserved / totalAvailable) * 100 : 0;

        return {
          productId: item.productId,
          productName: item.productName,
          totalAvailable,
          totalReserved,
          freeAvailable,
          reservationPercent,
        };
      });
    },
  },

  methods: {
    ...mapActions("messages", ["addErrorMessage"]),

    formatNumber(value) {
      if (value == null) return "0";
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    getReservationColor(percent) {
      if (percent >= 80) return "error";
      if (percent >= 50) return "warning";
      return "success";
    },

    navigateToIssues() {
      this.$router.push({ name: "inventoryReservationIssues" });
    },

    async loadDashboardData() {
      this.loading = true;
      try {
        await Promise.all([
          this.$store.dispatch(
            "inventoryReservation/fetchDashboardSummary"
          ),
          this.$store.dispatch(
            "inventoryReservation/fetchStaleReservations",
            7
          ),
          this.$store.dispatch(
            "inventoryReservation/fetchInventoryIssues"
          ),
        ]);
      } catch (error) {
        this.addErrorMessage(
          "Failed to load dashboard data. Please try again."
        );
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.loadDashboardData();
  },
};
</script>

<style scoped>
.summary-card {
  transition: box-shadow 0.2s ease;
}

.summary-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.summary-card .v-card__title {
  word-break: normal;
}

.summary-card .text-h4 {
  line-height: 1.2;
}
</style>
